/**
 * Unit conversion utilities for spec display.
 * Techspex data is mostly imperial; we convert to metric for display by default.
 */

// --- Conversion functions ---

export const inToMm = (val: number): number => val * 25.4;
export const mmToIn = (val: number): number => val / 25.4;
export const lbsToKg = (val: number): number => val * 0.4536;
export const kgToLbs = (val: number): number => val / 0.4536;
export const ipmToMmMin = (val: number): number => val * 25.4;
export const mmMinToIpm = (val: number): number => val / 25.4;
export const hpToKw = (val: number): number => val * 0.7457;
export const kwToHp = (val: number): number => val / 0.7457;
export const ftLbsToNm = (val: number): number => val * 1.3558;
export const nmToFtLbs = (val: number): number => val / 1.3558;
export const fToC = (val: number): number => (val - 32) * 5 / 9;
export const cToF = (val: number): number => val * 9 / 5 + 32;
export const galToL = (val: number): number => val * 3.7854;
export const lToGal = (val: number): number => val / 3.7854;
export const psiToBar = (val: number): number => val * 0.0689;
export const barToPsi = (val: number): number => val / 0.0689;

// --- Unit classification ---

/** Imperial units that we convert TO metric */
const IMPERIAL_UNITS: Record<string, { convert: (v: number) => number; metricUnit: string }> = {
  "in": { convert: inToMm, metricUnit: "mm" },
  '"': { convert: inToMm, metricUnit: "mm" },
  "inches": { convert: inToMm, metricUnit: "mm" },
  "inch": { convert: inToMm, metricUnit: "mm" },
  "lbs": { convert: lbsToKg, metricUnit: "kg" },
  "lb": { convert: lbsToKg, metricUnit: "kg" },
  "ipm": { convert: ipmToMmMin, metricUnit: "mm/min" },
  "hp": { convert: hpToKw, metricUnit: "kW" },
  "ft-lbs": { convert: ftLbsToNm, metricUnit: "Nm" },
  "ft·lbs": { convert: ftLbsToNm, metricUnit: "Nm" },
  "ft lbs": { convert: ftLbsToNm, metricUnit: "Nm" },
  "°f": { convert: fToC, metricUnit: "°C" },
  "gal": { convert: galToL, metricUnit: "L" },
  "gallons": { convert: galToL, metricUnit: "L" },
  "gallon": { convert: galToL, metricUnit: "L" },
  "psi": { convert: psiToBar, metricUnit: "bar" },
};

/** Metric units that we convert TO imperial */
const METRIC_UNITS: Record<string, { convert: (v: number) => number; imperialUnit: string }> = {
  "mm": { convert: mmToIn, imperialUnit: "in" },
  "kg": { convert: kgToLbs, imperialUnit: "lbs" },
  "mm/min": { convert: mmMinToIpm, imperialUnit: "ipm" },
  "kw": { convert: kwToHp, imperialUnit: "hp" },
  "nm": { convert: nmToFtLbs, imperialUnit: "ft-lbs" },
  "°c": { convert: cToF, imperialUnit: "°F" },
  "l": { convert: lToGal, imperialUnit: "gal" },
  "liters": { convert: lToGal, imperialUnit: "gal" },
  "liter": { convert: lToGal, imperialUnit: "gal" },
  "bar": { convert: barToPsi, imperialUnit: "psi" },
};

/** Smart rounding: 0 decimals for large values, 1-2 for small */
function smartRound(val: number): number {
  const abs = Math.abs(val);
  if (abs >= 100) return Math.round(val);
  if (abs >= 10) return Math.round(val * 10) / 10;
  return Math.round(val * 100) / 100;
}

export interface ConvertedSpec {
  displayValue: string;
  displayUnit: string;
}

/**
 * Convert a spec value based on the selected unit system.
 * @param numericValue The raw numeric value (null if non-numeric)
 * @param unit The original unit string from the data
 * @param toMetric true = display in metric, false = display in imperial
 * @returns The converted display value and unit, or originals if no conversion applies
 */
export function convertSpec(
  numericValue: number | null,
  unit: string,
  toMetric: boolean,
): ConvertedSpec {
  // Non-numeric values are never converted
  if (numericValue === null || numericValue === undefined) {
    return { displayValue: "", displayUnit: unit };
  }

  const unitLower = (unit || "").trim().toLowerCase();

  if (toMetric) {
    // Converting to metric: look for imperial units
    const imperialEntry = IMPERIAL_UNITS[unitLower];
    if (imperialEntry) {
      return {
        displayValue: String(smartRound(imperialEntry.convert(numericValue))),
        displayUnit: imperialEntry.metricUnit,
      };
    }
    // Already metric or unknown unit -- show as-is
    return { displayValue: String(numericValue), displayUnit: unit };
  } else {
    // Converting to imperial: look for metric units
    const metricEntry = METRIC_UNITS[unitLower];
    if (metricEntry) {
      return {
        displayValue: String(smartRound(metricEntry.convert(numericValue))),
        displayUnit: metricEntry.imperialUnit,
      };
    }
    // Already imperial or unknown unit -- show as-is
    return { displayValue: String(numericValue), displayUnit: unit };
  }
}
